<template>
  <v-autocomplete
    v-model="selected"
    :items="items"
    :loading="loading"
    :label="fieldLabel"
    :rules="rule"
    :multiple="!single"
    :clearable="!single"
    :filter="filterByNameAndTags"
    item-value="id"
    item-text="name"
    no-data-text="Ничего не найдено"
    prepend-inner-icon="mdi mdi-reload mdi-18px"
    @click:prepend-inner="refresh"
  >
    <template #selection="{ item, index }">
      <span v-if="single">{{ item.name }}</span>
      <v-chip v-else-if="index === 0" small style="max-width: 130px">
        <span class="sliced-name">{{ item.name }}</span>
      </v-chip>
      <span v-else-if="index === 1" class="grey--text caption">(+{{ selected.length - 1 }})</span>
    </template>

    <template #item="{ item, on, attrs }">
      <v-list-item v-bind="attrs" v-on="on">
        <v-list-item-content>
          <v-list-item-title>{{ item.name }}</v-list-item-title>
          <v-list-item-subtitle v-if="item.tags && item.tags.length">
            <v-chip
              v-for="tag in item.tags"
              :key="`${item.id}_${tag}`"
              x-small
              class="mr-1"
              outlined
            >
              {{ tag }}
            </v-chip>
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </template>

    <template v-if="!single && tags.length" #prepend-item>
      <div class="px-4 py-2">
        <div class="caption grey--text mb-1">Выбрать по тегу</div>
        <v-chip
          v-for="tag in tags"
          :key="`tag_${tag}`"
          small
          class="mr-1 mb-1"
          :color="isTagSelected(tag) ? 'primary' : undefined"
          :outlined="!isTagSelected(tag)"
          @click="toggleTag(tag)"
        >
          {{ tag }}
        </v-chip>
      </div>
      <v-divider class="mt-2" />
    </template>
  </v-autocomplete>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'ProjectTagSelector',

  props: {
    value: {
      required: true,
    },
    single: Boolean,
    noRules: Boolean,
    fieldLabel: {
      type: String,
      default: 'Проект',
    },
  },

  data() {
    return {
      items: [],
      loading: false,
      rule: [
        v => {
          if (this.noRules) return true
          if (this.single) return !!v || 'Данное поле обязательно'
          return !!(v && v.length) || 'Данное поле обязательно'
        },
      ],
    }
  },

  computed: {
    selected: {
      get() {
        return this.value
      },
      set(selected) {
        this.$emit('input', selected)
      },
    },

    tags() {
      const tags = new Set()
      this.items.forEach(item => (item.tags || []).forEach(tag => tags.add(tag)))
      return [...tags].sort((a, b) => a.localeCompare(b, 'ru'))
    },
  },

  methods: {
    ...mapActions({
      loadProjects: 'graphql/loadProjects',
    }),

    filterByNameAndTags(item, queryText) {
      const query = queryText.toLowerCase()
      if (String(item.name).toLowerCase().includes(query)) return true
      return (item.tags || []).some(tag => String(tag).toLowerCase().includes(query))
    },

    projectIdsByTag(tag) {
      return this.items.filter(item => (item.tags || []).includes(tag)).map(item => item.id)
    },

    isTagSelected(tag) {
      const ids = this.projectIdsByTag(tag)
      return ids.length > 0 && ids.every(id => this.selected.includes(id))
    },

    toggleTag(tag) {
      const ids = this.projectIdsByTag(tag)

      if (this.isTagSelected(tag)) {
        this.selected = this.selected.filter(id => !ids.includes(id))
        return
      }

      this.selected = [...new Set([...this.selected, ...ids])]
    },

    async refresh() {
      this.selected = this.single ? null : []
      await this.sendRequest()
    },

    async sendRequest() {
      this.loading = true

      const graphQuery = {
        query: `{
          projects {
            id
            name
            tags
          }
        }`,
      }

      try {
        const projects = await this.loadProjects(graphQuery)
        this.items = (projects || [])
          .slice()
          .sort((a, b) => String(a.name).localeCompare(String(b.name), 'ru'))
      } finally {
        this.loading = false
      }
    },
  },

  async mounted() {
    await this.sendRequest()
  },
}
</script>

<style scoped>
.sliced-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
